import { Link } from 'react-router-dom';
import { Badge, Button } from '@/components/ui';
import { buildSupportUrl } from '@/utils/supportUrl';
import { Info, Loader2, RefreshCw, Unplug } from 'lucide-react';
import type {
  AccountingIntegrationStatusResponse,
  AccountingProvider,
} from '../../../../../services/accountingIntegration';
import { AccountingProviderLogo } from './AccountingProviderLogo';
import { PROVIDER_LABELS } from './accountingIntegrationUi';

interface Props {
  academyId: string;
  status: AccountingIntegrationStatusResponse;
  lastCatalogRefreshAt: string | null;
  busy: boolean;
  refreshBusy: boolean;
  onReconnect: (provider: AccountingProvider) => void;
  onRefresh: () => void;
  onDisconnect: () => void;
}

function resolveConnectedProvider(
  status: AccountingIntegrationStatusResponse
): AccountingProvider | null {
  if (status.provider === 'xero' || status.provider === 'quickbooks') {
    return status.provider;
  }
  const platform = (status.platform ?? '').toLowerCase();
  if (platform.includes('xero')) return 'xero';
  if (platform.includes('quickbooks') || platform.includes('intuit')) return 'quickbooks';
  return null;
}

export function AccountingConnectionStatus({
  academyId,
  status,
  lastCatalogRefreshAt,
  busy,
  refreshBusy,
  onReconnect,
  onRefresh,
  onDisconnect,
}: Props) {
  const provider = resolveConnectedProvider(status);
  const platformLabel =
    status.platformName || (provider ? PROVIDER_LABELS[provider] : null) || status.platform || 'Connected';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {provider ? <AccountingProviderLogo provider={provider} className="h-9 w-9" /> : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900 dark:text-white">{platformLabel}</p>
            <Badge variant="success">Connected</Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {status.connectedAt
              ? `Connected ${new Date(status.connectedAt).toLocaleString()}`
              : 'Connected'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3 text-sm text-blue-950 dark:text-blue-100">
        <Info className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
        <div>
          <p className="font-medium">Daily sync schedule</p>
          <p className="mt-1 text-blue-900/90 dark:text-blue-100/90">
            Lesson payments, refunds, and payouts sync to your accounting software once per day
            (overnight UTC). New activity appears on the next scheduled run. Need syncs more often?{' '}
            <Link
              to={buildSupportUrl({
                type: 'feature_request',
                subject: 'More frequent accounting sync',
                message:
                  'We use the accounting integration and would like payments synced more frequently than the daily schedule.',
                academyId,
              })}
              className="font-medium text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100"
            >
              Request via support
            </Link>
            .
          </p>
        </div>
      </div>

      <div className="text-sm">
        <p className="text-gray-600 dark:text-gray-400">
          {status.lastSyncedAt
            ? `Last payment sync to your books: ${new Date(status.lastSyncedAt).toLocaleString()}`
            : 'Last payment sync to your books: not yet (no payments have finished syncing).'}
        </p>
        {lastCatalogRefreshAt ? (
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Last accounts and tax rates refresh: {new Date(lastCatalogRefreshAt).toLocaleString()}
          </p>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-xs">
            Last accounts and tax rates refresh: none recorded yet—use Refresh data to pull the latest
            from {platformLabel}.
          </p>
        )}
        {status.syncQueue && (status.syncQueue.pending > 0 || status.syncQueue.failed > 0) ? (
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-xs">
            Sync queue: {status.syncQueue.pending} pending
            {status.syncQueue.failed > 0
              ? `, ${status.syncQueue.failed} failed${
                  status.syncQueue.lastFailureAt
                    ? ` (last ${new Date(status.syncQueue.lastFailureAt).toLocaleString()})`
                    : ''
                }`
              : ''}
            . Failed items are retried on the next daily sync run; contact support if errors persist.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {provider ? (
          <Button
            variant="outline"
            onClick={() => onReconnect(provider)}
            disabled={busy || refreshBusy}
          >
            {busy && !refreshBusy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
            ) : null}
            Reconnect
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          onClick={onRefresh}
          disabled={busy || refreshBusy}
          title="Load the latest chart of accounts and tax rates from your accounting software (can take up to a minute)"
          aria-busy={refreshBusy}
        >
          {refreshBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          <span className="ml-1.5">{refreshBusy ? 'Refreshing…' : 'Refresh data'}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onDisconnect}
          disabled={busy || refreshBusy}
          className="text-red-600"
        >
          <Unplug className="h-4 w-4 mr-1 inline" aria-hidden />
          Disconnect
        </Button>
      </div>

      {refreshBusy ? (
        <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          Fetching the latest accounts and tax rates from your accounting software. This often finishes
          in a few seconds but can take longer for large organizations—we&apos;ll notify you when
          it&apos;s done.
        </p>
      ) : null}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">Refresh data</span> updates the
        chart of accounts and tax rates used for mapping (live from your accounting software). That
        fetch is separate from lesson payment sync—the{' '}
        <span className="font-medium text-gray-700 dark:text-gray-300">last payment sync</span> time and
        sync queue above refer to payments being sent to your books.
      </p>
    </div>
  );
}
